import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '../../lib/useApi'

const RANGES = [30, 90, 180]

function ymd(d) { return d.toISOString().slice(0, 10) }
function isSrank(values = {}) {
  // 7 criteria — same intent as legacy app
  const checks = [
    values.wake_ok,
    values.training_done,
    values.cardio_done,
    values.protein_ok,
    values.sleep_ok,
    values.no_phone_morning,
    values.diary_done,
  ]
  return checks.filter(Boolean).length >= 5
}

export default function StreakHeatmap() {
  const api = useApi()
  const [range, setRange] = useState(30)

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['daily-range', range],
    queryFn: () => api.get(`/daily/range?days=${range}`),
  })

  const map = useMemo(() => Object.fromEntries(rows.map((r) => [ymd(new Date(r.date)), r.values || {}])), [rows])

  const today = new Date()
  // Earliest tracked date — anything before is "not yet" rather than "missed".
  const firstTracked = rows.length > 0
    ? new Date(rows[0].date)
    : today
  const cells = useMemo(() => {
    const out = []
    for (let i = range - 1; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      const key = ymd(d)
      const v = map[key]
      const filled = v && Object.keys(v).length > 0
      const beforeStart = d < firstTracked && rows.length > 0
      const noHistoryYet = rows.length === 0
      out.push({
        date:    key,
        srank:   v ? isSrank(v) : false,
        filled,
        today:   i === 0,
        pending: beforeStart || noHistoryYet,   // never tracked yet → don't shame
      })
    }
    return out
  }, [range, map, firstTracked, rows.length])

  const { current, best, srankCount } = useMemo(() => {
    let cur = 0, best = 0, run = 0, srank = 0
    for (const c of cells) {
      if (c.filled) { run += 1; best = Math.max(best, run) } else { run = 0 }
      if (c.srank) srank += 1
    }
    cur = run
    return { current: cur, best, srankCount: srank }
  }, [cells])

  return (
    <section className="space-y-3">
      <div className="section-h">Streak</div>

      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setRange(r)}
                    className={`px-2 py-1 text-xs rounded ${range === r ? 'bg-[var(--accent)] text-black' : 'border border-white/15'}`}>
              {r}D
            </button>
          ))}
        </div>
        <div className="flex gap-4 text-xs num">
          <span>current <b className="glow">{current}</b></span>
          <span>best    <b>{best}</b></span>
          <span>S-rank  <b>{srankCount}</b></span>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-[3px] animate-pulse" style={{ gridTemplateColumns: `repeat(${Math.min(range, 30)}, minmax(0, 1fr))` }}>
          {Array.from({ length: range }).map((_, i) => (
            <div key={i} className="aspect-square rounded-[2px] bg-white/5" />
          ))}
        </div>
      ) : (
        <div className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${Math.min(range, 30)}, minmax(0, 1fr))` }}>
          {cells.map((c) => (
            <div key={c.date} title={`${c.date} ${c.srank ? '★ S-Rank' : c.pending ? '— pending' : !c.filled ? '✗ missed' : '· tracked'}`}
                 className={`aspect-square rounded-[2px] ${
                   c.pending
                     ? 'bg-white/5'
                     : !c.filled
                       ? 'bg-red-500/40'
                       : c.srank
                         ? 'bg-[var(--accent)]'
                         : 'bg-white/15'
                 } ${c.today ? 'ring-1 ring-[var(--accent)]' : ''}`} />
          ))}
        </div>
      )}
    </section>
  )
}
